import {
    Button,
    Checkbox,
    Divider,
    Empty,
    Input,
    List,
    message,
    Popconfirm,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd';
import {TeamOutlined, UserAddOutlined} from '@ant-design/icons';
import {useEffect, useState} from 'react';
import Helper from '../../func';
import RoleUserPicker from './UserPicker';

const {Text, Title} = Typography;
const {Search} = Input;

const RoleMembers = ({roleId, roleName}) => {
    const [members, setMembers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [keyword, setKeyword] = useState('');
    const [loading, setLoading] = useState(true);
    const [addingUsers, setAddingUsers] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);
    const pageSize = 10;
    const memberIds = [...new Set(members.map((user) => Number(user.u_id)))];
    const selectedOnPage = memberIds.filter((uId) => selectedMemberIds.includes(uId)).length;

    const loadMembers = (targetPage = page, targetKeyword = keyword) => {
        setLoading(true);
        Helper.http.get(Helper.serviceUrl('role/members'), {
            role_id: roleId,
            page: targetPage,
            page_size: pageSize,
            keyword: targetKeyword,
        }, (res) => {
            if (res.success) {
                setMembers(res.data || []);
                setTotal(Number(res.total) || 0);
            } else {
                message.error(res.msg || '成员列表加载失败');
            }
            setLoading(false);
        }, () => {
            setLoading(false);
            message.error('成员列表加载失败');
        });
    };

    useEffect(() => {
        setPage(1);
        setKeyword('');
        setAddingUsers(false);
        setSelectedMemberIds([]);
        loadMembers(1, '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleId]);

    const refreshAfterChange = () => {
        setSelectedMemberIds([]);
        loadMembers(1, keyword);
        setPage(1);
    };

    const addAllUsers = () => {
        if (saving) return;
        setSaving(true);
        Helper.http.post(Helper.serviceUrl('role/add_all_users'), {role_id: roleId}, (res) => {
            setSaving(false);
            if (!res.success) {
                message.error(res.msg || '添加失败');
                return;
            }
            message.success(res.msg || '添加完成');
            refreshAfterChange();
        }, () => {
            setSaving(false);
            message.error('添加失败');
        });
    };

    const removeUser = (uId) => {
        if (saving) return;
        setSaving(true);
        Helper.http.post(Helper.serviceUrl('role/remove_user'), {role_id: roleId, u_id: uId}, (res) => {
            setSaving(false);
            if (!res.success) {
                message.error(res.msg || '移除失败');
                return;
            }
            message.success(res.msg || '用户已移除');
            setSelectedMemberIds([]);
            const nextPage = members.length === 1 && page > 1 ? page - 1 : page;
            setPage(nextPage);
            loadMembers(nextPage, keyword);
        }, () => {
            setSaving(false);
            message.error('移除失败');
        });
    };

    const removeMembers = (removeAll = false) => {
        if (saving || (!removeAll && selectedMemberIds.length === 0)) return;
        setSaving(true);
        const endpoint = removeAll ? 'role/remove_all_users' : 'role/remove_users';
        const params = removeAll
            ? {role_id: roleId, confirm_all: 1}
            : {role_id: roleId, u_ids: selectedMemberIds};
        Helper.http.post(Helper.serviceUrl(endpoint), params, (res) => {
            setSaving(false);
            if (!res.success) {
                message.error(res.msg || '移除失败');
                return;
            }
            message.success(res.msg || '移除完成');
            refreshAfterChange();
        }, () => {
            setSaving(false);
            message.error('移除失败');
        });
    };

    const searchMembers = (value) => {
        const nextKeyword = value.trim();
        setKeyword(nextKeyword);
        setPage(1);
        setSelectedMemberIds([]);
        loadMembers(1, nextKeyword);
    };

    return <Spin spinning={saving}>
        <Title level={4} style={{marginTop: 0, marginBottom: 4}}>{roleName || '权限组'}</Title>
        <Text type="secondary">
            {keyword ? `找到 ${total} 名匹配成员` : `当前共有 ${total} 名成员`}
        </Text>

        <Divider orientation="left">添加成员</Divider>
        <Space wrap style={{width: '100%'}}>
            <Button type="primary" icon={<UserAddOutlined/>} disabled={saving}
                onClick={() => setAddingUsers(true)}>筛选并添加用户</Button>
            <Popconfirm
                title="确定将系统中的所有已启用账号加入该权限组吗？已禁用账号不会添加。"
                disabled={saving}
                onConfirm={addAllUsers}
                okText="全部添加"
                cancelText="取消"
            >
                <Button icon={<TeamOutlined/>} disabled={saving}>添加所有已启用用户</Button>
            </Popconfirm>
        </Space>

        <Divider orientation="left">成员列表</Divider>
        <Search
            allowClear
            placeholder="搜索账号或昵称"
            onSearch={searchMembers}
            style={{marginBottom: 16}}
        />
        <Space wrap style={{marginBottom: 16}}>
            <Checkbox
                checked={memberIds.length > 0 && selectedOnPage === memberIds.length}
                indeterminate={selectedOnPage > 0 && selectedOnPage < memberIds.length}
                disabled={loading || saving || memberIds.length === 0}
                onChange={(event) => setSelectedMemberIds(event.target.checked ? memberIds : [])}
            >本页全选</Checkbox>
            <Text type="secondary">已选 {selectedMemberIds.length} 名成员</Text>
            <Popconfirm
                title={`确定从该权限组移除所选的 ${selectedMemberIds.length} 名成员吗？`}
                disabled={loading || saving || selectedMemberIds.length === 0}
                onConfirm={() => removeMembers()}
                okText="移除所选"
                cancelText="取消"
            >
                <Button danger disabled={loading || saving || selectedMemberIds.length === 0}>批量移除所选成员</Button>
            </Popconfirm>
            <Popconfirm
                title="确定移除该权限组的所有成员吗？包含所有分页及搜索结果之外的成员，将回收他们通过该组获得的权限。"
                disabled={loading || saving}
                onConfirm={() => removeMembers(true)}
                okText="移除所有成员"
                cancelText="取消"
                okButtonProps={{danger: true}}
            >
                <Button danger disabled={loading || saving}>移除所有成员</Button>
            </Popconfirm>
        </Space>
        <List
            rowKey="role_user_id"
            loading={loading}
            dataSource={members}
            locale={{emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无成员"/>}}
            pagination={total > pageSize ? {
                current: page,
                pageSize,
                total,
                showSizeChanger: false,
                onChange: (nextPage) => {
                    setSelectedMemberIds([]);
                    setPage(nextPage);
                    loadMembers(nextPage, keyword);
                },
            } : false}
            renderItem={(user) => <List.Item
                actions={[
                    <Popconfirm
                        key="remove"
                        title="确定从该权限组移除此用户吗？"
                        onConfirm={() => removeUser(user.u_id)}
                        okText="移除"
                        cancelText="取消"
                    >
                        <Button type="link" danger disabled={loading || saving}>移除</Button>
                    </Popconfirm>,
                ]}
            >
                <Checkbox
                    aria-label={`选择成员 ${user.u_account}`}
                    checked={selectedMemberIds.includes(Number(user.u_id))}
                    disabled={loading || saving}
                    style={{marginRight: 12}}
                    onChange={(event) => {
                        const checked = event.target.checked;
                        const uId = Number(user.u_id);
                        setSelectedMemberIds((ids) => checked
                            ? [...new Set([...ids, uId])]
                            : ids.filter((id) => id !== uId));
                    }}
                />
                <List.Item.Meta
                    title={<Space>
                        <Text strong>{user.u_nickname || '未设置昵称'}</Text>
                        <Tag color={Number(user.u_status) === 9 ? 'green' : 'default'}>
                            {Number(user.u_status) === 9 ? '已启用' : '已禁用'}
                        </Tag>
                    </Space>}
                    description={user.u_account}
                />
            </List.Item>}
        />
        {addingUsers && <RoleUserPicker roleId={roleId} onClose={() => setAddingUsers(false)} onAdded={refreshAfterChange}/>}
    </Spin>;
};

export default RoleMembers;
